import React, { useState, useRef } from "react";
import {
  makeStyles,
  Paper,
  Typography,
  Button,
  Grid,
  CircularProgress,
  Box,
} from "@material-ui/core";
import {
  Backup as BackupIcon,
  Restore as RestoreIcon,
  GetApp as DownloadIcon,
  CloudUpload as UploadIcon,
} from "@material-ui/icons";
import { toast } from "react-toastify";
import api from "../../../services/api";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  section: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  sectionTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  description: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
  },
  dangerZone: {
    borderColor: theme.palette.error.main,
    border: "1px solid",
  },
  dangerTitle: {
    color: theme.palette.error.main,
  },
  hiddenInput: {
    display: "none",
  },
}));

const BackupRestore = () => {
  const classes = useStyles();
  const fileInputRef = useRef(null);

  const [loadingSQL, setLoadingSQL] = useState(false);
  const [loadingJSON, setLoadingJSON] = useState(false);
  const [loadingRestore, setLoadingRestore] = useState(false);

  const handleBackupSQL = async () => {
    setLoadingSQL(true);

    try {
      const response = await api.get("/backup/sql", {
        responseType: "blob",
      });

      const contentDisposition =
        response.headers["content-disposition"];

      const filename = contentDisposition
        ? contentDisposition.split("filename=")[1]?.replace(/"/g, "")
        : `backup_${Date.now()}.sql`;

      const url = window.URL.createObjectURL(
        new Blob([response.data])
      );

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);

      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Backup SQL baixado com sucesso!");
    } catch (err) {
      console.error(err);
      toast.error("Erro ao gerar backup SQL.");
    }

    setLoadingSQL(false);
  };

  const handleBackupJSON = async () => {
    setLoadingJSON(true);

    try {
      const response = await api.get("/backup/json", {
        responseType: "blob",
      });

      const contentDisposition =
        response.headers["content-disposition"];

      const filename = contentDisposition
        ? contentDisposition.split("filename=")[1]?.replace(/"/g, "")
        : `backup_${Date.now()}.json`;

      const url = window.URL.createObjectURL(
        new Blob([response.data])
      );

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);

      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Backup JSON baixado com sucesso!");
    } catch (err) {
      console.error(err);
      toast.error("Erro ao gerar backup JSON.");
    }

    setLoadingJSON(false);
  };

  const handleRestore = async (e) => {
    const targetInput = e?.target || fileInputRef.current;
    const file = targetInput?.files?.[0];

    if (!file) return;

    if (!file.name.endsWith(".sql")) {
      toast.error("Apenas arquivos .sql são aceitos.");
      if (targetInput) targetInput.value = "";
      return;
    }

    const confirmRestore = window.confirm(
      "⚠️ ATENÇÃO: A restauração irá sobrescrever o banco atual. Deseja continuar?"
    );

    if (!confirmRestore) {
      if (targetInput) targetInput.value = "";
      return;
    }

    setLoadingRestore(true);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await api.post(
        "/backup/restore",
        formData,
        {
          timeout: 300000,
        }
      );

      toast.success(
        "Banco restaurado com sucesso! Recarregue a página."
      );

      console.info(
        "Restore success:",
        response?.data
      );
    } catch (err) {
      const message =
        err?.response?.data?.error ||
        err?.message ||
        "Erro ao restaurar o banco de dados.";

      console.error(
        "Restore error:",
        err?.response?.data || err
      );

      toast.error(message);
    } finally {
      setLoadingRestore(false);
      try {
        if (targetInput) targetInput.value = "";
      } catch (_) {}
      try {
        if (fileInputRef.current) fileInputRef.current.value = "";
      } catch (_) {}
    }
  };

  return (
    <div className={classes.root}>
      <Paper className={classes.section} elevation={1}>
        <div className={classes.sectionTitle}>
          <BackupIcon color="primary" />
          <Typography variant="h6">
            Backup do Banco de Dados
          </Typography>
        </div>

        <Typography className={classes.description}>
          Faça o download de uma cópia completa do banco.
        </Typography>

        <Grid container spacing={2}>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              startIcon={
                loadingSQL ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <DownloadIcon />
                )
              }
              onClick={handleBackupSQL}
              disabled={loadingSQL || loadingJSON}
            >
              {loadingSQL ? "Gerando..." : "Backup SQL"}
            </Button>
          </Grid>

          <Grid item>
            <Button
              variant="outlined"
              color="primary"
              startIcon={
                loadingJSON ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <DownloadIcon />
                )
              }
              onClick={handleBackupJSON}
              disabled={loadingSQL || loadingJSON}
            >
              {loadingJSON ? "Gerando..." : "Backup JSON"}
            </Button>
          </Grid>
        </Grid>

        <Box mt={2}>
          <Typography variant="caption">
            💡 SQL é recomendado para restauração completa.
          </Typography>
        </Box>
      </Paper>

      <Paper
        className={`${classes.section} ${classes.dangerZone}`}
        elevation={1}
      >
        <div className={classes.sectionTitle}>
          <RestoreIcon color="error" />
          <Typography
            variant="h6"
            className={classes.dangerTitle}
          >
            Restaurar Banco de Dados
          </Typography>
        </div>

        <Typography className={classes.description}>
          ⚠️ Essa ação irá substituir os dados atuais.
        </Typography>

        <input
          ref={fileInputRef}
          accept=".sql"
          className={classes.hiddenInput}
          id="restore-file-input"
          type="file"
          onChange={handleRestore}
          disabled={loadingRestore}
        />

        <label htmlFor="restore-file-input">
          <Button
            variant="contained"
            color="secondary"
            component="span"
            startIcon={
              loadingRestore ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <UploadIcon />
              )
            }
            disabled={loadingRestore}
          >
            {loadingRestore
              ? "Restaurando..."
              : "Enviar arquivo .sql"}
          </Button>
        </label>
      </Paper>
    </div>
  );
};

export default BackupRestore;